import { useMutation, useQueryClient } from 'react-query';

import { FormikHelpers } from 'formik';
import { AllowanceBudgetType, AllowancePayload } from 'services/api';
import useApi from 'src/hooks/useApi';
import useNotification from 'src/hooks/useNotification';
import AllowanceQueryKeys from 'src/query-keys/allowance';

import {
  AllowanceDetailForm,
  AllowanceValuesType
} from './AllowanceDetailForm';
import { AllowanceFormProps } from './types';

export const allowanceInitialValues: AllowanceValuesType = {
  editable: true,
  name: '',
  budget: 0,
  input_budget: '',
  budget_type: AllowanceBudgetType.Daily,
  allowance_date_from: '',
  allowance_date_to: '',
  applicable_weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  applicable_all_day_time: false,
  applicable_time_from: '09:00',
  applicable_time_to: '18:00',
  applicable_users: [],
  applicable_type: 'group',
  applicable_company_locations: [],
  allow_flexible_locations: true,
  is_restaurant: true,
  is_shop: true,
  is_catering: false,
  is_delivery: true,
  is_pickup: true,
  expense_code_required_in_checkout: false,
  is_voucher: false,
  meals_included: true,
  state: 'active',
  company_departments: [],
  company_locations: [],
  ordering_rule_company_department: []
};

export const AddAllowanceForm = ({
  formRef,
  setAction,
  editable,
  onQuery,
  ...others
}: AllowanceFormProps) => {
  const api = useApi();
  const { pushNotification, dismissAllNotifications, dismissNotification } =
    useNotification();
  const queryClient = useQueryClient();

  const addAllowance = useMutation((payload: AllowancePayload) =>
    api.company.allowance.create(payload)
  );

  const handleSubmitAdd = (
    values: AllowanceValuesType,
    actions: FormikHelpers<AllowanceValuesType>
  ) => {
    const snackbarKey = pushNotification(`Adding ${values.name}...`, {
      variant: 'info'
    });

    addAllowance.mutate(values, {
      onSuccess: () => {
        queryClient.invalidateQueries(AllowanceQueryKeys.list()).then(() => {
          dismissNotification(snackbarKey);
          pushNotification(`Allowance added`, {
            variant: 'success'
          });
          setAction(false);
          onQuery('');
        });
      },
      onError: (e) => {
        dismissNotification(snackbarKey);

        const error = e as Error;
        pushNotification(`Error adding ${values.name}, ${error.message}`, {
          variant: 'error',
          onClick: () => dismissAllNotifications()
        });
      },
      onSettled: () => {
        actions.setSubmitting(false);
      }
    });
  };

  allowanceInitialValues.editable = editable;

  return (
    <AllowanceDetailForm
      initialValues={allowanceInitialValues}
      handleSubmit={handleSubmitAdd}
      ref={formRef}
      onQuery={onQuery}
      {...others}
    />
  );
};
